import {
    Button,
    ColorPicker,
    ColorUtils,
    FabButton,
    RestaurantPublicMenu,
    SectionLabel,
    SelectFontFamily,
    Spinner,
    TextField,
    useConfirmContext,
} from '@localina/core';
import { SelectChangeEvent } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Color } from 'react-color';
import { useTranslation } from 'react-i18next';
import { useRestaurantMenus, useUpdateMenuConfigurationMutation } from '../../../api/queries/restaurantMenu';
import { useRestaurant } from '../../../api/queries/restaurants';
import { SaveIcon } from '@localina/icons';

type TMenuConfigurationForm = {
    tabTextFontFamily?: string;
    backgroundColor?: Color;
    defaultTabTextColor?: Color;
    activeTabTextColor?: Color;
    cssUrl?: string;
};

const defaultMenuConfiguration = {
    backgroundColor: '#FFFFFF',
    // From the redesign mock-up: 15.9:1 and 7.4:1 on the white background.
    defaultTabTextColor: '#17272F',
    activeTabTextColor: '#005E6E',
    tabTextFontFamily: 'Roboto',
    cssUrl: '',
};
type TMenuAppearanceProps = {
    setContentNotSaved: Dispatch<SetStateAction<boolean>>;
};
export const MenuAppearance = (props: TMenuAppearanceProps) => {
    const { t, i18n } = useTranslation();
    const { snackbar } = useConfirmContext();
    const restaurantQuery = useRestaurant();
    const restaurantMenusQuery = useRestaurantMenus();

    const updateMenuConfigurationMutation = useUpdateMenuConfigurationMutation();

    const [menuConfiguration, setMenuConfiguration] = React.useState<TMenuConfigurationForm>(defaultMenuConfiguration);
    const [initialMenuConfiguration, setInitialMenuConfiguration] =
        React.useState<TMenuConfigurationForm>(defaultMenuConfiguration);

    const changeMenuConfiguration =
        (field: keyof TMenuConfigurationForm) => (value: TMenuConfigurationForm[typeof field]) => {
            setMenuConfiguration((prev) => ({ ...prev, [field]: value }));
        };

    const onTabFontFamilyChange = (event: SelectChangeEvent) => {
        changeMenuConfiguration('tabTextFontFamily')(event.target.value);
    };

    const [untestedCssUrl, setUntestedCssUrl] = useState<string | undefined>(undefined);

    const testCssUrl = () => {
        changeMenuConfiguration('cssUrl')(untestedCssUrl);
    };

    const handleSave = () => {
        updateMenuConfigurationMutation.mutate(
            {
                tabTextFontFamily: menuConfiguration.tabTextFontFamily || null,
                backgroundColor: menuConfiguration.backgroundColor ? String(menuConfiguration.backgroundColor) : null,
                defaultTabTextColor: menuConfiguration.defaultTabTextColor
                    ? String(menuConfiguration.defaultTabTextColor)
                    : null,
                activeTabTextColor: menuConfiguration.activeTabTextColor
                    ? String(menuConfiguration.activeTabTextColor)
                    : null,
                cssUrl: menuConfiguration.cssUrl || null,
            },
            {
                onSuccess: () => {
                    snackbar({
                        msg: t('integrations.items.menu.infoMessages.savedMenuSettingsSuccess'),
                        severity: 'success',
                    });
                },
                onError: () => {
                    snackbar({ msg: t('integrations.items.menu.infoMessages.errorMessage'), severity: 'error' });
                },
            },
        );
    };

    useEffect(() => {
        const isContentChanged = () =>
            initialMenuConfiguration.tabTextFontFamily !== menuConfiguration.tabTextFontFamily ||
            initialMenuConfiguration.backgroundColor !== menuConfiguration.backgroundColor ||
            initialMenuConfiguration.defaultTabTextColor !== menuConfiguration.defaultTabTextColor ||
            initialMenuConfiguration.activeTabTextColor !== menuConfiguration.activeTabTextColor ||
            initialMenuConfiguration.cssUrl !== menuConfiguration.cssUrl;

        props.setContentNotSaved(isContentChanged());
    }, [
        menuConfiguration.tabTextFontFamily,
        menuConfiguration.backgroundColor,
        menuConfiguration.defaultTabTextColor,
        menuConfiguration.activeTabTextColor,
        menuConfiguration.cssUrl,
        initialMenuConfiguration,
    ]);

    useEffect(() => {
        if (restaurantQuery.data?.configuration.menuConfig) {
            const configuration = {
                tabTextFontFamily:
                    restaurantQuery.data.configuration.menuConfig.tabTextFontFamily ??
                    defaultMenuConfiguration.tabTextFontFamily,
                backgroundColor:
                    restaurantQuery.data.configuration.menuConfig.backgroundColor ??
                    defaultMenuConfiguration.backgroundColor,
                defaultTabTextColor:
                    restaurantQuery.data.configuration.menuConfig.defaultTabTextColor ??
                    defaultMenuConfiguration.defaultTabTextColor,
                activeTabTextColor:
                    restaurantQuery.data.configuration.menuConfig.activeTabTextColor ??
                    defaultMenuConfiguration.activeTabTextColor,
                cssUrl: restaurantQuery.data.configuration.menuConfig.cssUrl ?? defaultMenuConfiguration.cssUrl,
            };
            setMenuConfiguration(configuration);
            setInitialMenuConfiguration(configuration);
        } else {
            setInitialMenuConfiguration(defaultMenuConfiguration);
        }
    }, [restaurantQuery.data?.configuration.menuConfig]);

    const menuItems = restaurantMenusQuery.data?.pages[0]?.items || [];

    const renderContrastHint = (color?: Color) => {
        const ratio = ColorUtils.contrastRatio(String(color), String(menuConfiguration.backgroundColor));
        if (ratio === undefined) {
            return null;
        }
        const isLow = ratio < ColorUtils.WCAG_AA_NORMAL_TEXT;
        const formatted = new Intl.NumberFormat(i18n.language, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
        }).format(ratio);
        return (
            <p className={`integration-hint${isLow ? ' is-warning' : ''}`}>
                {t(`integrations.items.menu.tabs.appearance.labels.${isLow ? 'contrastLow' : 'contrast'}`, {
                    ratio: formatted,
                })}
            </p>
        );
    };

    return (
        <div className={'appearance-tab-container'}>
            <div className={'public-preview'}>
                <SectionLabel value={t('integrations.items.menu.tabs.appearance.labels.previewTitle')} />
                <RestaurantPublicMenu
                    menuConfiguration={{
                        menu: menuItems,
                        tabTextFontFamily: menuConfiguration.tabTextFontFamily,
                        backgroundColor: String(menuConfiguration.backgroundColor),
                        defaultTabTextColor: String(menuConfiguration.defaultTabTextColor),
                        activeTabTextColor: String(menuConfiguration.activeTabTextColor),
                        cssUrl: menuConfiguration.cssUrl,
                    }}
                />
                {!menuItems.length && !restaurantMenusQuery.isInitialLoading && (
                    <p className="public-preview__empty">
                        {t('integrations.items.menu.tabs.appearance.labels.previewEmpty')}
                    </p>
                )}
            </div>

            <div className={'additional-settings'}>
                <ColorPicker
                    title={t('integrations.items.menu.tabs.appearance.labels.backgroundColorPicker')}
                    onChange={changeMenuConfiguration('backgroundColor')}
                    value={menuConfiguration.backgroundColor}
                />
                <div className="appearance-color">
                    <ColorPicker
                        title={t('integrations.items.menu.tabs.appearance.labels.dateTabTextColor')}
                        onChange={changeMenuConfiguration('defaultTabTextColor')}
                        value={menuConfiguration.defaultTabTextColor}
                    />
                    {renderContrastHint(menuConfiguration.defaultTabTextColor)}
                </div>
                <div className="appearance-color">
                    <ColorPicker
                        title={t('integrations.items.menu.tabs.appearance.labels.activeTabColor')}
                        onChange={changeMenuConfiguration('activeTabTextColor')}
                        value={menuConfiguration.activeTabTextColor}
                    />
                    {renderContrastHint(menuConfiguration.activeTabTextColor)}
                </div>
                <div className={'select-font-family-wrapper'}>
                    <span className="appearance-field-label">
                        {t('integrations.items.menu.tabs.appearance.labels.tabTextFontFamily')}
                    </span>
                    <SelectFontFamily value={menuConfiguration.tabTextFontFamily} onChange={onTabFontFamilyChange} />
                </div>
                <div className={'css-url-wrapper'}>
                    <TextField
                        value={untestedCssUrl}
                        onChange={setUntestedCssUrl}
                        name={'cssUrl'}
                        label={t('integrations.items.menu.tabs.appearance.labels.cssUrl')}
                    />
                    <div className={'buttons'}>
                        <Button
                            label={t('integrations.items.menu.tabs.appearance.labels.testCssButton')}
                            onClick={testCssUrl}
                            secondary
                        />
                    </div>
                </div>
                <div className={'action-buttons-wrapper'}>
                    <div className={'buttons'}>
                        <Button label={t('common.buttons.save')} onClick={handleSave} />
                    </div>
                </div>
                <FabButton icon={<SaveIcon />} onClick={handleSave} className="save-fab-button" />
            </div>
            {updateMenuConfigurationMutation.isLoading && <Spinner />}
        </div>
    );
};
